// src/components/lists/BeingsList/ClientsList.tsx

import React, { useState, useEffect, useRef } from 'react';
import { Typography, Box, CircularProgress } from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  ContentCopy as CopyIcon,
  WarningAmberOutlined as WarningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../../theme/theme';
import {
  BeingsListPrimaryButton,
  BeingsListSecondaryButton,
  BeingsListTertiaryButton,
} from '../../common/Buttons';
import CopyButton from '../../common/CopyButton';

import {
  ListContainer,
  BeingRow,
  RowClickableArea,
  BeingNameSection,
  BeingIcon,
  BeingName,
  SkeletonBeingName,
  ActionButtons,
  ContactDetailsSection,
  ReservationsSection,
  ReservationsList,
  ReservationItem,
  ReservationDate,
  ContactSection,
  ContactItem,
  PetCountDisplay,
  isBeingDataLoaded,
  ItemWarningIcon,
} from './beingsListStyles';

import {
  useBeingsListState,
  useBeingsListUtils,
  getClientContactInfo,
  getClientLastBookings,
  createKeyDownHandler,
  getAccessibilityProps,
} from './beingsListBase';

import { ClientsListProps } from './beingsListTypes';
import { styled } from '@mui/material/styles';
import { BookingListItem } from '../../../services/api';
import { formatBookingDateRangeString } from '../BookingsList/bookingsListUtils';

const EnhancedBeingRow = styled(BeingRow)<{
  isOpen?: boolean;
  isEven?: boolean;
}>`
  &:hover {
    .pet-count-display {
      opacity: ${(props) => (props.isOpen ? 1 : 1)} !important;
    }
  }
`;

const BookingRow = styled(ReservationItem)(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(28, 46, 74, 0.3)'
        : theme.palette.grey[500],
  },
}));

const BookingDateRange = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
  justifyContent: 'space-between',
});

const ClientsList: React.FC<ClientsListProps> = ({
  items,
  onEdit,
  onView,
  onDelete,
  onNewBooking,
  onViewBookings,
  onViewAdmin,
  onViewMedical,
  onOwnerClick,
  onPetClick,
  openItemId: openItemIdProp,
  onOpenChange,
}) => {
  // Controlled when openItemIdProp is defined (number | null);
  // uncontrolled when undefined (fall back to internal state).
  const isControlled = openItemIdProp !== undefined;
  const internal = useBeingsListState();
  const openItemId = isControlled
    ? (openItemIdProp ?? null)
    : internal.openItemId;

  const setOpenItemId = (id: number | null) => {
    if (isControlled) {
      onOpenChange?.(id);
    } else {
      internal.setOpenItemId(id);
      onOpenChange?.(id);
    }
  };

  const utils = useBeingsListUtils(openItemId, setOpenItemId);
  const navigate = useNavigate();

  const [bookingsCache, setBookingsCache] = useState<
    Record<number, BookingListItem[]>
  >({});
  const [loadingBookings, setLoadingBookings] = useState<
    Record<number, boolean>
  >({});
  const bookingsRef = useRef<Record<number, BookingListItem[]>>({});

  const listPetNames = (client: any): string => {
    const pets = client.pets || [];
    if (!pets || pets.length === 0) return '(No pets)';

    const petNames = pets
      .map((pet: any) => pet.name?.trim())
      .filter((name: string | undefined) => !!name && name.length > 0);

    return petNames.length > 0 ? `(${petNames.join(', ')})` : '(Unnamed pets)';
  };

  useEffect(() => {
    if (
      openItemId &&
      !bookingsRef.current[openItemId] &&
      !loadingBookings[openItemId]
    ) {
      setLoadingBookings((prev) => ({ ...prev, [openItemId]: true }));

      getClientLastBookings(openItemId)
        .then((bookings) => {
          bookingsRef.current[openItemId] = bookings;
          setBookingsCache((prev) => ({ ...prev, [openItemId]: bookings }));
        })
        .catch(() => {
          bookingsRef.current[openItemId] = [];
          setBookingsCache((prev) => ({ ...prev, [openItemId]: [] }));
        })
        .finally(() => {
          setLoadingBookings((prev) => ({ ...prev, [openItemId]: false }));
        });
    }
  }, [openItemId, loadingBookings]);

  return (
    <ListContainer>
      {items.map((client, index) => {
        const itemId = client.id;
        const isOpen = openItemId === itemId;
        const isEven = index % 2 === 0;
        const clientName = utils.renderName(client);
        const contactInfo = getClientContactInfo(client);
        const petNamesText = listPetNames(client);
        const dataLoaded = isBeingDataLoaded(client, 'clients');
        const clientBookings = bookingsCache[itemId] || [];
        const isLoadingBookings = loadingBookings[itemId] || false;

        return (
          <EnhancedBeingRow
            key={itemId}
            isOpen={isOpen}
            isEven={isEven}
            {...getAccessibilityProps(isOpen, clientName)}
            id={`client-row-${itemId}`}
            onKeyDown={createKeyDownHandler(
              isOpen,
              itemId,
              utils.handleNameClick,
              utils.handleRowClick
            )}
          >
            <RowClickableArea
              isOpen={isOpen}
              onClick={() => utils.handleRowClick(itemId)}
            />

            <BeingNameSection
              className="being-name-section"
              isOpen={isOpen}
              onClick={
                isOpen
                  ? (e) => utils.handleNameClick(itemId, e)
                  : (e) => {
                      e.stopPropagation();
                      utils.handleRowClick(itemId);
                    }
              }
            >
              <BeingIcon isOpen={isOpen}>
                <PersonIcon />
              </BeingIcon>
              {client.showWarning && (
                <ItemWarningIcon isOpen={isOpen}>
                  <WarningIcon />
                </ItemWarningIcon>
              )}
              <BeingName isOpen={isOpen}>
                {dataLoaded ? (
                  <>
                    <span className="surname">{client.lastName}</span>,{' '}
                    {client.firstName}
                    <PetCountDisplay
                      isOpen={isOpen}
                      className="pet-count-display"
                    >
                      {petNamesText}
                    </PetCountDisplay>
                  </>
                ) : (
                  <SkeletonBeingName
                    isOpen={isOpen}
                    widthVariant="long"
                    animation="wave"
                  />
                )}
              </BeingName>
            </BeingNameSection>

            {isOpen && (
              <ActionButtons>
                <BeingsListSecondaryButton
                  minWidth="250px"
                  onClick={() => onNewBooking?.(itemId)}
                >
                  NEW BOOKING
                </BeingsListSecondaryButton>

                <BeingsListPrimaryButton
                  minWidth="200px"
                  onClick={() => onViewBookings?.(itemId)}
                >
                  BOOKINGS
                </BeingsListPrimaryButton>

                <BeingsListPrimaryButton
                  minWidth="176px"
                  onClick={() => navigate(`/clients/${itemId}/account`)}
                >
                  ACCOUNT
                </BeingsListPrimaryButton>

                <BeingsListTertiaryButton
                  minWidth="154px"
                  onClick={() => navigate(`/clients/${itemId}/quotations`)}
                >
                  QUOTES
                </BeingsListTertiaryButton>

                <BeingsListTertiaryButton
                  minWidth="150px"
                  onClick={() => navigate(`/clients/${itemId}/pets`)}
                >
                  PETS
                </BeingsListTertiaryButton>

                <BeingsListTertiaryButton
                  minWidth="150px"
                  onClick={() => onView?.(itemId)}
                >
                  VIEW
                </BeingsListTertiaryButton>

                <BeingsListTertiaryButton
                  minWidth="150px"
                  onClick={() => onEdit?.(itemId)}
                >
                  EDIT
                </BeingsListTertiaryButton>
              </ActionButtons>
            )}

            <ReservationsSection isOpen={isOpen}>
              <ReservationsList>
                {isLoadingBookings ? (
                  <ReservationItem>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                      }}
                    >
                      <ReservationDate>Loading bookings...</ReservationDate>
                      <CircularProgress
                        size={16}
                        thickness={5}
                        sx={{ color: colors.primary.main }}
                      />
                    </Box>
                  </ReservationItem>
                ) : clientBookings.length === 0 ? (
                  <ReservationItem>
                    <ReservationDate>No bookings</ReservationDate>
                  </ReservationItem>
                ) : (
                  clientBookings.map((booking) => (
                    <BookingRow
                      key={booking.id}
                      onClick={() => navigate(`/booking/${booking.id}`)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/booking/${booking.id}`);
                        }
                      }}
                      aria-label={`View booking: ${formatBookingDateRangeString(booking)}`}
                    >
                      <BookingDateRange>
                        <ReservationDate>
                          {formatBookingDateRangeString(booking)}
                        </ReservationDate>
                      </BookingDateRange>
                    </BookingRow>
                  ))
                )}
              </ReservationsList>
            </ReservationsSection>

            <ContactDetailsSection isOpen={isOpen}>
              <ContactSection>
                {client.showWarning && (
                  <ContactItem>
                    <WarningIcon />
                    <Typography variant="body1" fontWeight={600}>
                      {client.notes}
                    </Typography>
                  </ContactItem>
                )}

                <ContactItem>
                  <PhoneIcon
                    sx={{ fontSize: '1.4rem', color: colors.primary.main }}
                  />
                  <Typography variant="body1" fontWeight={600}>
                    {contactInfo.phone}
                  </Typography>
                  {contactInfo.hasPhone && (
                    <CopyButton
                      valueToCopy={contactInfo.phone}
                      icon={<CopyIcon fontSize="small" />}
                      size="small"
                      aria-label="Copy phone number"
                    />
                  )}
                </ContactItem>

                <ContactItem>
                  <EmailIcon
                    sx={{ fontSize: '1.4rem', color: colors.primary.main }}
                  />
                  <Typography variant="body1" fontWeight={600}>
                    {contactInfo.email}
                  </Typography>
                  {contactInfo.hasEmail && (
                    <CopyButton
                      valueToCopy={contactInfo.email}
                      icon={<CopyIcon fontSize="small" />}
                      size="small"
                      aria-label="Copy email address"
                    />
                  )}
                </ContactItem>
              </ContactSection>
            </ContactDetailsSection>
          </EnhancedBeingRow>
        );
      })}
    </ListContainer>
  );
};

export default ClientsList;
